import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Buildings, ArrowRight, Lock, User, Warning, ArrowsClockwise } from "@phosphor-icons/react";

const HERO_BG =
  "https://images.unsplash.com/photo-1727522974614-b592018e49e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50JTIwZ2VvbWV0cnl8ZW58MHx8fHwxNzc3MjY4OTk3fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [email, setEmail] = useState("admin@itc.aiverse");
  const [password, setPassword] = useState("Admin@123");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { user, login, logout, loading } = useAuth();
  const nav = useNavigate();

  // Auto-redirect if already authenticated (prevents getting stuck on /)
  useEffect(() => {
    if (!loading && user) {
      nav(user.role === "admin" ? "/admin" : "/chat", { replace: true });
    }
  }, [user, loading, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      const u = await login(cleanEmail, password);
      toast.success(`Welcome back, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : "/chat", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = detail || err?.message || "Login failed";
      if (status === 401) msg = "Wrong email or password — try again, or use a Demo button below.";
      else if (!err?.response) msg = "Cannot reach server. Check your connection or refresh the page.";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (role) => {
    setError("");
    if (role === "admin") {
      setEmail("admin@itc.aiverse");
      setPassword("Admin@123");
    } else {
      setEmail("user@itc.aiverse");
      setPassword("User@123");
    }
  };

  const resetSession = () => {
    logout();
    localStorage.clear();
    setError("");
    toast.success("Session reset — please log in again");
    setTimeout(() => window.location.reload(), 400);
  };

  return (
    <div className="h-screen grid grid-cols-1 lg:grid-cols-2 overflow-hidden">
      {/* Left - Form */}
      <div className="flex items-center justify-center px-8 py-8 bg-white overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-slate-900 flex items-center justify-center">
              <Buildings weight="bold" size={22} className="text-white" />
            </div>
            <div>
              <div className="mono-label text-slate-500">3CC Tech</div>
              <div className="font-display text-lg leading-none">AiVerse</div>
            </div>
          </div>

          <div className="mb-2 mono-label text-orange-600">// access · knowledge complex</div>
          <h1 className="font-display text-4xl md:text-5xl mb-3 text-slate-900">
            Enter the<br />Digital AI Verse
          </h1>
          <p className="text-slate-600 mb-6 leading-relaxed">
            A residential complex of knowledge — Lobby, Sector, Block, Building, Flat.
            Every byte has an address.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="mono-label block mb-2">Email</label>
              <div className="flex items-center border border-slate-300 px-3 focus-within:border-slate-900 transition-all" style={{ borderRadius: 2 }}>
                <User size={18} className="text-slate-400" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-3 py-3 outline-none bg-transparent text-slate-900"
                  placeholder="you@itc.aiverse"
                  required
                />
              </div>
            </div>
            <div>
              <label className="mono-label block mb-2">Password</label>
              <div className="flex items-center border border-slate-300 px-3 focus-within:border-slate-900 transition-all" style={{ borderRadius: 2 }}>
                <Lock size={18} className="text-slate-400" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex-1 px-3 py-3 outline-none bg-transparent text-slate-900"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="border border-orange-300 bg-orange-50 px-3 py-2 flex items-start gap-2" data-testid="login-error">
                <Warning size={16} weight="fill" className="text-orange-600 mt-0.5 shrink-0" />
                <div className="text-sm text-orange-900">{error}</div>
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={busy}
              className="w-full btn-sharp btn-primary py-3.5 font-semibold disabled:opacity-50"
            >
              {busy ? "Authenticating…" : "Enter Complex"}
              <ArrowRight size={18} weight="bold" />
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="mono-label mb-3">Quick demo access</div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="demo-admin-btn"
                onClick={() => fillDemo("admin")}
                className="btn-sharp btn-secondary text-sm flex-1"
              >
                Admin demo
              </button>
              <button
                type="button"
                data-testid="demo-user-btn"
                onClick={() => fillDemo("user")}
                className="btn-sharp btn-secondary text-sm flex-1"
              >
                User demo
              </button>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="mono-label text-slate-500">
                Trouble logging in?
              </div>
              <button
                type="button"
                onClick={resetSession}
                className="mono-label text-orange-600 hover:underline inline-flex items-center gap-1"
                data-testid="reset-session-btn"
              >
                <ArrowsClockwise size={10} />
                Reset session & reload
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right - Hero */}
      <div className="hidden lg:block relative bg-slate-900 overflow-hidden">
        <img
          src={HERO_BG}
          alt="Architectural blueprint"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 blueprint-grid-dense" />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/70 via-slate-900/30 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="mono-label text-orange-400">// 3CCTech.ITC.AiVerse</div>
          <div>
            <div className="max-w-md">
              <div className="font-display text-3xl text-white leading-tight tracking-tight">
                A knowledge address<br />for every fact.
              </div>
              <div className="text-slate-400 mt-3 text-sm">
                Retrieve in milliseconds. Cite to the flat.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
